//region imports
import React, { useEffect, useRef, useState } from 'react';
import "./SearchDetails.css";
import queryString from 'query-string';
import { ProvidersService } from '../services/ProvidersService';
import ToggleButton from '@mui/material/ToggleButton';
import { Alert, Box, CircularProgress, IconButton, Link, Pagination, Rating, TextField, ThemeProvider, ToggleButtonGroup, Typography } from "@mui/material";
import { Button } from "@mui/material";
import FavoriteIcon from '@mui/icons-material/Favorite';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { createTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import UseWindowDimensions from "../components/GetWindowDimensions";
import { CalendarService } from "../services/CalendarService";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Constants } from "../Constants";
import { useKeycloak } from "@react-keycloak/web";
import PhotoViewer from "../components/PhotoViewer";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Map from "../components/Map";
import { getConfig } from '../configService';
import { getSlotThumbnailUrl } from '../utils/slotThumbnail';
import { PatternFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { TelemetryService } from "../services/TelemetryService";
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import NavigationIcon from '@mui/icons-material/Navigation';
import ShareIcon from '@mui/icons-material/Share'
import ClaimBusinessDialog from '../components/ClaimBusinessDialog';
import TurnstileWidget from '../components/TurnstileWidget';
//endregion

//region styles and themes
const theme = createTheme({
  palette: {
    unfav: {
      main: '#8f8f8f'
    },
    fav: {
      main: '#ff0f0f'
    },
  },
});

//endregion

const VERIFY_DIALOG_TITLE = "Verify You're Human";
const VERIFY_DIALOG_TEXT = "Complete this quick check to see this business's contact details.";
const VERIFY_FAILED_MESSAGE = "We couldn't verify you. Please try again.";
const CONTACT_UNAVAILABLE_MESSAGE = "Contact details are unavailable right now. Please try again later.";
const EMPTY_CONTACT = { phoneNumber: "", email: "" };

//region dialogs
function ContactDialog(props) {
  const { title, text, handleClose, open } = props;

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>{title}</DialogTitle>
      <Box style={{ display: "flex", margin: "auto", marginLeft: "14px", marginRight: "14px", marginBottom: "14px" }}>{text}</Box>
    </Dialog>
  );
}
//endregion

function SearchDetails() {
  const parsed = queryString.parse(window.location.search);
  const { friendlyUrl } = useParams();
  const itemID = parsed.id;
  const schedulingSlotId = parsed.schedulingSlotId ?? null;
  const { height, width } = UseWindowDimensions();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  //region state
  const [provider, setProvider] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedLength, setSelectedLength] = useState(null);
  const [selectedSchedulingSlot, setSelectedSchedulingSlot] = useState(null);
  const [showLengthSelector, setShowLengthSelector] = useState(false);
  const [showAppointmentSelector, setShowAppointmentSelector] = useState(false);
  const [showBookingButton, setShowBookingButton] = useState(false);
  const [lengthList, setLengthList] = useState([]);
  const [availableAppointmentsList, setAvailableAppointmentsList] = useState([]);
  const [price, setPrice] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState(null);
  const [verifyAttempt, setVerifyAttempt] = useState(0);
  const [contactLoading, setContactLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [appointment, setAppointment] = useState(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [reviewStars, setReviewStars] = useState(5);
  const [reviewerName, setReviewerName] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewPage, setReviewPage] = useState(1);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const lastTrackedProviderViewRef = useRef("");
  const currentProviderIdRef = useRef(null);
  //endregion
  const currentReviewerSub = keycloak?.tokenParsed?.sub;
  const currentUserReview = keycloak.authenticated && currentReviewerSub
    ? provider?.reviews?.find((review) => review.reviewerSub === currentReviewerSub)
    : null;
  const sortedReviews = provider?.reviews?.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];
  const reviewPageSize = 10;
  const reviewPageCount = Math.ceil(sortedReviews.length / reviewPageSize);
  const pagedReviews = sortedReviews.slice((reviewPage - 1) * reviewPageSize, reviewPage * reviewPageSize);
  const providerId = provider?.id || itemID;
  const shareUrl = provider?.id ? `${window.location.origin}/details?id=${encodeURIComponent(provider.id)}` : "";

  //region effects

  useEffect(() => {
    if (itemID) {
      ProvidersService.getSingleProvider(itemID).then((response) => setProvider(response));
    } else if (friendlyUrl) {
      ProvidersService.getSingleProviderByFriendlyUrl(friendlyUrl).then((response) => setProvider(response));
    }
  }, [itemID, friendlyUrl]);

  useEffect(() => {
    if (!itemID || !provider?.friendlyUrl) {
      return;
    }

    const queryParams = new URLSearchParams();
    if (schedulingSlotId) {
      queryParams.set("schedulingSlotId", schedulingSlotId);
    }

    const query = queryParams.toString();
    navigate(`/${encodeURIComponent(provider.friendlyUrl)}${query ? `?${query}` : ""}`, { replace: true });
  }, [itemID, provider, schedulingSlotId, navigate]);

  useEffect(() => {
    if (!provider?.id || lastTrackedProviderViewRef.current === provider.id) {
      return;
    }

    lastTrackedProviderViewRef.current = provider.id;
    const route = window.location.pathname || "unknown_route";

    TelemetryService.sendEvent({
      name: "provider_view",
      route,
      page: route,
      status: "ok",
      tags: {
        provider_id: provider.id,
        friendly_url: provider.friendlyUrl || "",
      },
    });
  }, [provider?.id, provider?.friendlyUrl]);

  useEffect(() => {
    if (provider?.schedulingSlots && provider.schedulingSlots.length > 0) {
      const activeSlots = provider.schedulingSlots.filter(s => s.status !== 0);
      let slotToSelect = null;
      if (schedulingSlotId) {
        slotToSelect = activeSlots.find(s => s.id === schedulingSlotId);
      }
      if (!slotToSelect) {
        slotToSelect = activeSlots.find(s => s.name?.toLowerCase() === "default") || activeSlots[0];
      }
      setSelectedSchedulingSlot(slotToSelect);
    }
  }, [provider, schedulingSlotId]);

  useEffect(() => {
    setShowAppointmentSelector(selectedLength != null);
    setPrice(selectedLength?.price);
  }, [selectedLength]);

  useEffect(() => {
    setShowLengthSelector(lengthList?.length > 1);
  }, [lengthList])

  useEffect(() => {
    let s = selectedService;
    CreateLengthToggleButtonsList(s);
    setSelectedLength(s?.offeredLengths?.length <= 1 ? s.offeredLengths[0] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService])
  //endregion

  useEffect(() => {
    setShowBookingButton(appointment);
  }, [appointment])

  useEffect(() => {
    if (provider && provider.googlePlacesId) {
      setGoogleMapsUrl("https://www.google.com/maps/search/?api=1&query=Google&query_place_id=" + provider.googlePlacesId.replace("places/", ""));
    }
    else {
      setGoogleMapsUrl("");
    }
  }, [provider])

  useEffect(() => {
    if (currentUserReview) {
      setReviewStars(currentUserReview.stars);
      setReviewerName(currentUserReview.reviewerName || "");
      setReviewComment(currentUserReview.comment || "");
    }
  }, [currentUserReview]);

  useEffect(() => {
    setReviewPage(1);
  }, [provider?.id]);

  const providerKey = provider?.id;
  const contactRedacted = provider?.contactRedacted === true;
  const providerPhone = provider?.phoneNumber || "";
  const providerEmail = provider?.email || "";

  useEffect(() => {
    // Lets in-flight contact requests detect that the displayed business has changed.
    currentProviderIdRef.current = providerKey ?? null;
  }, [providerKey]);

  // Owners (and admins) receive the real values, so their dialogs open straight away. Anonymous
  // visitors start empty; a revealed contact only counts for the business it was revealed for.
  const contactKnown = !contactRedacted || revealed?.providerId === providerKey;
  const contact = !contactRedacted
    ? { phoneNumber: providerPhone, email: providerEmail }
    : (contactKnown ? revealed : EMPTY_CONTACT);

  const onFavoriteClick = () => {
    setIsFavorite(!isFavorite)
  }

  function CreateLengthToggleButtonsList(s) {
    let listOfLengths = s?.offeredLengths?.map((l) => <ToggleButton
      value={l}>{l.length} Minutes</ToggleButton>);
    setLengthList(listOfLengths);
  }

  //region eventHandlers
  const onServiceClick = (e, s) => {
    setSelectedService(s);
  };

  const onLengthClick = (e, l) => {
    setSelectedLength(l);
  };

  const openContactDialog = (target) => {
    if (contactKnown) {
      if (target === "phone") {
        setPhoneOpen(true);
      } else {
        setEmailOpen(true);
      }
      return;
    }
    setVerifyTarget(target);
    setVerifyStatus(null);
    setVerifyAttempt((attempt) => attempt + 1);
    setVerifyOpen(true);
  }

  const openPhoneDialog = () => {
    openContactDialog("phone");
  }

  const closePhoneDialog = () => {
    setPhoneOpen(false);
  }

  const openEmailDialog = () => {
    openContactDialog("email");
  }

  const closeEmailDialog = () => {
    setEmailOpen(false);
  }

  const closeVerifyDialog = () => {
    setVerifyOpen(false);
    setVerifyStatus(null);
  }

  const onVerifyWidgetError = () => {
    setVerifyStatus("verify");
  }

  const retryContactVerification = () => {
    setVerifyStatus(null);
    setVerifyAttempt((attempt) => attempt + 1);
  }

  const onContactTokenReceived = async (token) => {
    const requestedProviderId = provider?.id;
    const target = verifyTarget;
    setContactLoading(true);
    setVerifyStatus(null);
    try {
      const result = await ProvidersService.revealContact(requestedProviderId, token);
      if (currentProviderIdRef.current !== requestedProviderId) {
        return;
      }
      setRevealed({ providerId: requestedProviderId, phoneNumber: result?.phoneNumber || "", email: result?.email || "" });
      setVerifyOpen(false);
      if (target === "email") {
        setEmailOpen(true);
      } else {
        setPhoneOpen(true);
      }
    } catch (error) {
      if (currentProviderIdRef.current !== requestedProviderId) {
        return;
      }
      // 403 (check failed) and 400 (bad token) both mean the visitor needs a fresh check.
      const status = error?.response?.status;
      setVerifyStatus(status === 403 || status === 400 ? "verify" : "unavailable");
    } finally {
      setContactLoading(false);
    }
  }

  const closeShareDialog = () => {
    setShareOpen(false);
  }

  const onDateSelected = async (d) => {
    let availableSlots = await CalendarService.getAvailableAppointments(providerId, d.$y, d.$M + 1, d.$D, selectedLength.length, selectedSchedulingSlot?.id);
    setAvailableAppointmentsList(availableSlots.map((s) => <ToggleButton sx={{ width: "60px" }} value={s}>{s.split('T')[1].substring(0, 5)}</ToggleButton>));
  }

  const onBookAppointmentClick = async () => {
    if (provider?.schedulingSlots && provider.schedulingSlots.length > 0 && !selectedSchedulingSlot?.id) {
      console.error("A scheduling slot must be selected");
      return;
    }
    var bookingData = {
      "selectedService": selectedService,
      "selectedLength": selectedLength,
      "appointment": appointment,
      "providerId": provider.id,
      "schedulingSlotId": selectedSchedulingSlot?.id || null
    };
    localStorage.setItem("bookingData", JSON.stringify(bookingData));

    navigate("/bookAppointment");
  }

  const onAppointmentSelected = (e, a) => {
    setAppointment(a);
  }

  const onSchedulingSlotSelected = (e, slot) => {
    if (slot === null) return;
    setSelectedSchedulingSlot(slot);
    setAppointment(null);
    setAvailableAppointmentsList([]);
  }

  const onWebsiteClick = () => {
    TelemetryService.sendEvent({
      name: "button_click",
      route: window.location.pathname || "unknown_route",
      page: window.location.pathname || "unknown_route",
      status: "ok",
      tags: {
        button_text: "Website",
        button_id: "none",
        button_type: "button",
        provider_id: provider?.id || itemID || "",
      },
    });

    window.open(provider.websiteUrl, "_blank");
  };

  const onGoogleMapsClick = () => {
    TelemetryService.sendEvent({
      name: "button_click",
      route: window.location.pathname || "unknown_route",
      page: window.location.pathname || "unknown_route",
      status: "ok",
      tags: {
        button_text: "View on Google Maps",
        button_id: "none",
        button_type: "button",
        provider_id: provider?.id || itemID || "",
      },
    });

    window.open(googleMapsUrl, "_blank");
  };

  const onShareClick = async () => {
    if (!shareUrl) {
      return;
    }

    TelemetryService.sendEvent({
      name: "button_click",
      route: window.location.pathname || "unknown_route",
      page: window.location.pathname || "unknown_route",
      status: "ok",
      tags: {
        button_text: "Share",
        button_id: "none",
        button_type: "button",
        provider_id: provider?.id || itemID || "",
      },
    });

    if (navigator.share) {
      try {
        await navigator.share({
          title: provider?.name || "PairCare Provider",
          text: provider?.name ? `View ${provider.name} on PairCare` : "View this provider on PairCare",
          url: shareUrl
        });
        return;
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage("Share link copied to clipboard.");
    } catch {
      setShareMessage(shareUrl);
    }
    setShareOpen(true);
  };

  const onSubmitReview = async () => {
    if (!provider?.id || !reviewStars) {
      setReviewMessage("Please select a star rating.");
      return;
    }

    try {
      if (!keycloak.authenticated) {
        keycloak.login({ redirectUri: window.location.href });
        return;
      }

      const updatedProvider = await ProvidersService.addProviderReview(provider.id, {
        stars: reviewStars,
        reviewerName,
        comment: reviewComment
      }, keycloak);
      setProvider(updatedProvider);
      setReviewMessage(currentUserReview ? "Your review has been updated." : "Thank you for leaving a review.");
    } catch {
      setReviewMessage("Unable to save your review. Please try again.");
    }
  };
  //endregion

  const listOfServices = provider.servicesProvided?.map((servicesProvided) =>
    <ToggleButton value={servicesProvided}>{servicesProvided.name}</ToggleButton>
  );

  const config = getConfig();
  const hasPhoneContact = Boolean(provider.hasPhone || (provider.phoneNumber && provider.phoneNumber !== "5555555555"));
  const hasEmailContact = Boolean(provider.hasEmail || (provider.email && provider.email !== "none@none.com"));
  const listOfImages = provider.imageList?.map((image) => config.imageBaseUrl + provider.id + "/" + image + "-large.jpg");

  return (
    <Box className="search-details-main">
      <Helmet>
        <title>{`PairCare - ${provider?.name}`}</title>
      </Helmet>
      {showBookingButton && <Box className="sticky" style={{ top: height - 70 }}>
        <Button sx={{
          marginTop: "1em", width: width - 20, maxWidth: 918,
          margin: "auto", display: "flex", backgroundColor: '#f97316',
          '&:hover': { backgroundColor: '#ea670c' }
        }} variant="contained" onClick={onBookAppointmentClick}>${price} - Book Appointment</Button>
      </Box>}
      <Box>
        <Box style={{ display: "flex", flexWrap: "wrap", margin: "10px", marginTop: "0" }}>
          <Box style={{ display: "inline-flex" }}>
            <h1 id="detailsTitle" >{provider?.name}</h1>
          </Box>
          <Box style={{ verticalAlign: "middle", marginTop: "auto", marginBottom: "auto", marginLeft: "10px", marginRight: "10px", display: "inline-flex" }}> {provider?.rating?.toFixed(1)}
            <Box className="stars-outer">
              <Box className="stars-inner" style={{ width: (Math.round(((provider?.rating / 5) * 67))) }}></Box>
            </Box>
          </Box>
          {keycloak.authenticated && <Box style={{ verticalAlign: "middle", marginTop: "auto", marginBottom: "auto", marginLeft: "auto", marginRight: "10px" }}>
            <ThemeProvider theme={theme}>
              <Box sx={{ borderRadius: '8px', border: 0, borderColor: "primary.main" }}  >
                <IconButton aria-label="favorite" color={isFavorite ? "fav" : "unfav"} onClick={onFavoriteClick}>
                  <FavoriteIcon />
                </IconButton>
              </Box>
            </ThemeProvider>
          </Box>
          }
        </Box>

        <PhotoViewer images={listOfImages} />

        <Box style={{ paddingLeft: "10px", paddingRight: "10px" }}>
          <Box className="page-segment">
            <h5>About {provider.name}</h5>
            <p>{provider.businessSummary}</p>
            {!provider.claimedAt && <Link component="button" onClick={() => setClaimDialogOpen(true)}>Claim this business</Link>}
          </Box>

          {provider.schedulingSlots && provider.schedulingSlots.filter(s => s.status !== 0).length > 1 && <Box className="page-segment">
            <h5>Provider</h5>
            <ToggleButtonGroup color={"primary"} id="slotSelector" fullWidth={true} orientation={width <= Constants.MOBILE_WIDTH ? "vertical" : "horizontal"} value={selectedSchedulingSlot} exclusive onChange={onSchedulingSlotSelected}>
              {provider.schedulingSlots.filter(s => s.status !== 0).map((slot) => {
                const SLOT_AVATAR_SIZE = 40;
                const thumbnailUrl = getSlotThumbnailUrl(slot, providerId);
                return (
                  <ToggleButton
                    key={slot.id}
                    value={slot}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'visible',
                      ...(thumbnailUrl && {
                        minHeight: SLOT_AVATAR_SIZE,
                        paddingLeft: `${SLOT_AVATAR_SIZE + 12}px`
                      })
                    }}
                  >
                    {thumbnailUrl && (
                      <img
                        src={thumbnailUrl}
                        alt={slot.name}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        style={{
                          position: 'absolute',
                          left: '-1px',
                          top: '-1px',
                          height: 'calc(100% + 2px)',
                          width: 'auto',
                          aspectRatio: '1',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    {slot.name}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>}

          {provider.servicesProvided?.length > 0 && <Box className="page-segment">
            <h5>Services</h5>
            <ToggleButtonGroup color={"primary"} id="serviceSelector" fullWidth={true} orientation={width <= Constants.MOBILE_WIDTH ? "vertical" : "horizontal"} value={selectedService} exclusive onChange={onServiceClick}>{listOfServices}</ToggleButtonGroup>
            {showLengthSelector && <p><br /><b>{selectedService?.name}</b> - {selectedService?.description}</p>}
          </Box>}
          {showLengthSelector && <Box className="page-segment">
            <h5>Appointment Length</h5>
            <ToggleButtonGroup color={"primary"} id="lengthSelector" fullWidth={true} orientation={width <= Constants.MOBILE_WIDTH ? "vertical" : "horizontal"} value={selectedLength} exclusive onChange={onLengthClick}>{lengthList}</ToggleButtonGroup>
          </Box>}
          {showAppointmentSelector && <Box className="page-segment">
            <LocalizationProvider dateAdapter={AdapterDayjs} >
              <DateCalendar onChange={onDateSelected} minDate={dayjs(new Date())} />
            </LocalizationProvider>
            <Box style={{ marginTop: "10px", marginLeft: "auto", marginRight: "auto", width: "280px" }}>
              <ToggleButtonGroup sx={{ display: "flex", gap: "14px" }} color={"primary"} id="appSelector" value={appointment} exclusive onChange={onAppointmentSelected}>
                {availableAppointmentsList}
              </ToggleButtonGroup>
            </Box>
          </Box>}
          <Box height={"200px"} width={"100%"} margin={"auto"} marginBottom={"5px"} >
            {provider.latitude !== undefined && <Map center={{ longitude: provider.longitude, latitude: provider.latitude }} zoom={15} places={[{ longitude: provider.longitude, latitude: provider.latitude, display_name: provider?.name }]} showPopup={false} />}
          </Box>
          <Box className="page-segment">
            {shareUrl && <Button fullWidth={width <= Constants.MOBILE_WIDTH} style={{ margin: "10px" }} variant="contained" endIcon={<ShareIcon />} onClick={onShareClick}>Share</Button>}
            {provider.websiteUrl && <Button fullWidth={width <= Constants.MOBILE_WIDTH} style={{ margin: "10px" }} variant="contained" endIcon={<OpenInBrowserIcon />} onClick={onWebsiteClick}>Website</Button>}
            {hasPhoneContact && <Button fullWidth={width <= Constants.MOBILE_WIDTH} style={{ margin: "10px" }} variant="contained" endIcon={<PhoneIcon />} onClick={openPhoneDialog}>Phone</Button>}
            {hasEmailContact && <Button fullWidth={width <= Constants.MOBILE_WIDTH} style={{ margin: "10px" }} variant="contained" endIcon={<EmailIcon />} onClick={openEmailDialog}>Email</Button>}
            {provider.googlePlacesId && <Button fullWidth={width <= Constants.MOBILE_WIDTH} style={{ margin: "10px" }} variant="contained" endIcon={<NavigationIcon />} onClick={onGoogleMapsClick}>View On Google Maps</Button>}
          </Box>
          <Box className="page-segment">
            <h5>Reviews</h5>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography>{provider?.rating?.toFixed(1) || "0.0"}</Typography>
              <Rating value={provider?.rating || 0} precision={0.5} readOnly />
              {sortedReviews.length > 0 && <Typography>({sortedReviews.length})</Typography>}
            </Box>
            {pagedReviews.length > 0 && pagedReviews.map((review, index) => (
              <Box key={review.id} sx={{ py: 2, borderBottom: index < pagedReviews.length - 1 ? "1px solid #ddd" : "none" }}>
                <Rating value={review.stars} readOnly />
                <Typography fontWeight={600}>{review.reviewerName || "Anonymous"}</Typography>
                {review.comment && <Typography>{review.comment}</Typography>}
              </Box>
            ))}
            {reviewPageCount > 1 && <Pagination
              count={reviewPageCount}
              page={reviewPage}
              onChange={(event, page) => setReviewPage(page)}
              sx={{ mt: 2, mb: 2 }}
            />}
            {keycloak.authenticated ? <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
              <Typography fontWeight={600}>{currentUserReview ? "Edit your review" : "Leave a review"}</Typography>
              <Rating value={reviewStars} onChange={(event, value) => setReviewStars(value || 0)} precision={1} />
              <TextField label="Your name" value={reviewerName} onChange={(event) => setReviewerName(event.target.value)} />
              <TextField label="Review" value={reviewComment} onChange={(event) => setReviewComment(event.target.value)} />
              <Button variant="contained" onClick={onSubmitReview}>{currentUserReview ? "Update Review" : "Submit Review"}</Button>
              {reviewMessage && <Alert severity={reviewMessage.startsWith("Thank") || reviewMessage.startsWith("Your") ? "success" : "error"}>{reviewMessage}</Alert>}
            </Box> : <Box sx={{ mt: 2 }}>
              <Typography sx={{ mb: 1 }}>Sign in to leave a review.</Typography>
              <Button variant="contained" onClick={() => keycloak.login({ redirectUri: window.location.href })}>Sign In To Review</Button>
            </Box>}
          </Box>
          <Box style={{ height: 70 }} />
        </Box>

      </Box>

      {/* Todo: This needs to be refactored to use material UI. */}

      <ContactDialog title="Phone Number" text={<Link style={{ margin: "10px" }} href={"tel:+" + contact.phoneNumber}><PatternFormat
        value={contact.phoneNumber}
        displayType="text"  // Ensure it's not an input
        format="(###) ###-####"  // Reusing the format from your component
      /></Link>} open={phoneOpen} handleClose={closePhoneDialog} />
      <ContactDialog title="Email Address" text={<a style={{ margin: "10px" }} href={"mailto:" + contact.email}>{contact.email}</a>} open={emailOpen} handleClose={closeEmailDialog} />
      {/* Mounted only while open: an always-mounted MUI Dialog shifts React useId values for the page's other snapshots. */}
      {verifyOpen && <Dialog open onClose={closeVerifyDialog}>
        <DialogTitle>{VERIFY_DIALOG_TITLE}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{VERIFY_DIALOG_TEXT}</Typography>
          {verifyStatus === "verify" && <Alert severity="error" sx={{ mb: 2 }}>{VERIFY_FAILED_MESSAGE}</Alert>}
          {verifyStatus === "unavailable" && <Alert severity="error" sx={{ mb: 2 }}>{CONTACT_UNAVAILABLE_MESSAGE}</Alert>}
          {contactLoading && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress aria-label="Loading contact details" />
          </Box>}
          {!contactLoading && verifyStatus !== "unavailable" && <TurnstileWidget
            key={verifyAttempt}
            siteKey={getConfig().turnstileSiteKey}
            action="reveal-contact"
            onToken={onContactTokenReceived}
            onError={onVerifyWidgetError}
          />}
          {verifyStatus && <Button sx={{ mt: 2 }} variant="contained" onClick={retryContactVerification}>Try Again</Button>}
        </DialogContent>
      </Dialog>}
      <ContactDialog title="Share" text={<span style={{ margin: "10px" }}>{shareMessage}</span>} open={shareOpen} handleClose={closeShareDialog} />
      <ClaimBusinessDialog open={claimDialogOpen} onClose={() => setClaimDialogOpen(false)} provider={provider} />
    </Box>
  )
}

export default SearchDetails;






